import React, { useState, useMemo, useEffect, useCallback } from "react";
import { ContainersTable } from "./containers.table.admin";
import { ContainerStatusFilter } from "./container.status-filter.admin";
import { ContainerDialog } from "./container.dialog.admin";
import {
  containerGrpc,
  useListContainersQuery,
} from "../../../../shared/api/container/container.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useAccumulatedList } from "~/shared/hooks/use-accumulated-list";
import { useListContainerTypesQuery } from "../../../../shared/api/container-type/container-type.grpc";
import { useListUsersQuery } from "~/shared/api/user/user.grpc";
import { useSelectedCompany } from "../../../../shared/store/company/hooks/useSelectedCompany";
import { type FilterFilterParam, type SortingState } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { useContainerUrl } from "../../model/useContainerUrl";
import {
  CONTAINER_SORT_BY,
  driverFullName,
  tabListRequest,
  type ContainerServerFilters,
} from "../../model/container.filters";
import { ConnectProto } from "@racoon/api";
import { toListSort } from "~/shared/lib/list-sort";

type Container = ConnectProto.Container;

const CONTAINERS_PAGE_SIZE = 100;

const selectRows = (data: ConnectProto.ContainerList) => data.containers;
const selectTotal = (data: ConnectProto.ContainerList) => data.listInfo?.totalItems;
const selectRowKey = (container: Container) => String(container.id);

interface ContainersSettingsProps {
  selectedCompanyId?: number;
}

export const ContainersSettings: React.FC<ContainersSettingsProps> = ({
  selectedCompanyId: propSelectedCompanyId,
}) => {
  const { selectedCompanyId: storeSelectedCompanyId } = useSelectedCompany();
  const resolvedCompanyId = propSelectedCompanyId ?? storeSelectedCompanyId ?? undefined;
  const {
    containerTab,
    setContainerTab,
    containerSearch,
    setContainerSearch,
    containerTypeIds,
    setContainerTypeIds,
    pickupLocationIds,
    setPickupLocationIds,
    driverIds,
    setDriverIds,
  } = useContainerUrl();
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Search is server-side (ListContainers matches the container name and its
  // pickup location name), so debounce the URL value to avoid an RPC per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(containerSearch);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(containerSearch), 300);
    return () => clearTimeout(timer);
  }, [containerSearch]);

  // Every filter is server-side, so the tab badges count exactly the rows the
  // tab returns.
  const serverFilters: ContainerServerFilters = {
    ownerOrganizationIds: resolvedCompanyId ? [BigInt(resolvedCompanyId)] : [],
    containerTypeIds: containerTypeIds.map(BigInt),
    pickupLocationIds: pickupLocationIds.map(BigInt),
    driverIds: driverIds.map(BigInt),
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
  };

  const listArg = { ...serverFilters, ...tabListRequest(containerTab) };
  const [sorting, setSorting] = useState<SortingState>([]);
  const sort = toListSort(sorting, CONTAINER_SORT_BY);

  // Pages of 100 in the server's order. The server applies every filter and the
  // column sort, so a page is a true slice of the list.
  const list = useAccumulatedList<ConnectProto.ContainerList, Container>({
    useListQuery: useListContainersQuery,
    listEndpoint: containerGrpc.endpoints.listContainers,
    arg: { ...listArg, ...(sort && { listFilter: sort }) },
    pageSize: CONTAINERS_PAGE_SIZE,
    filterKey: [
      resolvedCompanyId,
      containerTab,
      containerTypeIds,
      pickupLocationIds,
      driverIds,
      debouncedSearch.trim(),
      sort?.sortBy,
      sort?.sortDirection,
    ].join("|"),
    selectRows,
    selectTotal,
    selectRowKey,
    skip: !resolvedCompanyId,
  });

  const { currentData: { containerTypes = [] } = {} } = useListContainerTypesQuery(
    { ownerOrganizationIds: resolvedCompanyId ? [BigInt(resolvedCompanyId)] : [] },
    { skip: !resolvedCompanyId },
  );

  // Sites, like drivers, come from their own list rather than the loaded rows: a
  // page holds only some of them. This is the organization's shared site list,
  // cached once for every screen that asks for it.
  const { currentData: { pickupLocations = [] } = {} } = useListPickupLocationsQuery(
    { serviceProviderIds: resolvedCompanyId ? [BigInt(resolvedCompanyId)] : [] },
    { skip: !resolvedCompanyId },
  );

  // Fetched rather than read off the rows: driverIds is a server filter, so the
  // loaded rows only ever hold the driver already selected and the option list
  // would collapse to it. No status filter — a driver who has since been
  // deactivated still serviced the containers this filter has to find.
  const { currentData: { users: drivers = [] } = {} } = useListUsersQuery(
    {
      ownerOrganizationIds: resolvedCompanyId ? [BigInt(resolvedCompanyId)] : [],
      roles: [ConnectProto.UserRole.DRIVER],
    },
    { skip: !resolvedCompanyId },
  );

  const handleRowSelect = (container: Container | null) => {
    setSelectedContainer(container);
  };

  const [editingContainer, setEditingContainer] = useState<Container | null>(null);

  const handleCreateClick = () => {
    setEditingContainer(null);
    setDialogOpen(true);
  };

  const handleEditContainer = useCallback((container: Container) => {
    setEditingContainer(container);
    setDialogOpen(true);
  }, []);

  const t = useT();

  // ===== FilterBlock wiring =====
  // Status is not among these: the tabs above the table already are the status
  // filter. Sites and drivers come from their own lists — see the queries above;
  // the site filter matches a container's *registered* location.
  const filterParams = useMemo<FilterFilterParam[]>(() => {
    const containerTypeOptions = containerTypes.map((type) => ({
      id: String(type.id),
      name: type.name || `#${type.id}`,
    }));
    const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
    const pickupLocationOptions = pickupLocations
      .map((location) => ({ id: String(location.id), name: location.name || `#${location.id}` }))
      .sort(byName);
    const driverOptions = drivers
      .map((driver) => ({ id: String(driver.id), name: driverFullName(driver) }))
      .sort(byName);

    return [
      {
        key: "containerType",
        searchable: true,
        label: t(Namespace.Settings, "inventory.filters.containerType"),
        type: "select",
        options: containerTypeOptions,
        value: containerTypeIds[0] ? String(containerTypeIds[0]) : "",
      },
      {
        key: "pickupLocation",
        searchable: true,
        label: t(Namespace.Settings, "inventory.filters.pickupLocation"),
        type: "select",
        options: pickupLocationOptions,
        value: pickupLocationIds[0] ? String(pickupLocationIds[0]) : "",
      },
      {
        key: "driver",
        searchable: true,
        label: t(Namespace.Settings, "inventory.filters.driver"),
        type: "select",
        options: driverOptions,
        value: driverIds[0] ? String(driverIds[0]) : "",
      },
    ];
  }, [t, containerTypes, pickupLocations, drivers, containerTypeIds, pickupLocationIds, driverIds]);

  const handleFilterParamsChange = (key: string, value: string | null) => {
    switch (key) {
      case "containerType":
        setContainerTypeIds(value ? [Number(value)] : []);
        break;
      case "pickupLocation":
        setPickupLocationIds(value ? [Number(value)] : []);
        break;
      case "driver":
        setDriverIds(value ? [Number(value)] : []);
        break;
    }
  };

  return (
    <div className="gap-4 flex flex-col p-4 bg-white h-full rounded-lg w-full border">
      <ContainerStatusFilter
        activeTab={containerTab}
        onTabChange={setContainerTab}
        searchValue={containerSearch}
        onSearchChange={setContainerSearch}
        countFilters={resolvedCompanyId ? serverFilters : undefined}
        filterParams={filterParams}
        onFilterParamsChange={handleFilterParamsChange}
        onCreateClick={handleCreateClick}
      />

      <ContainersTable
        containers={list.rows}
        isLoading={list.isFetching && list.rows.length === 0}
        loadingMore={list.isFetching && list.rows.length > 0}
        onEndReached={list.hasMore ? list.loadNextPage : undefined}
        onSortChange={setSorting}
        selectedContainer={selectedContainer}
        onRowSelect={handleRowSelect}
        onEditClick={handleEditContainer}
      />

      <ContainerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedCompanyId={resolvedCompanyId}
        container={editingContainer}
      />
    </div>
  );
};
